<template>
  <!-- 塔罗增强分享卡: 正位 → gen.mjs B1「悬浮牌面」(真牌面聚光+红章), 逆位 → B2「真实逆位」(牌面物理180°+黑章),
       长牌义 → B3 验证过的 2行clamp+「长按识别二维码」引导行。视觉/CSS 逐字节移植, 勿"优化"实测尺寸 -->
  <div v-if="visible" class="tsc-mask" @click.self="close">
    <div class="tsc-dialog">
      <div class="tsc-head">
        <span class="tsc-title">分享我的牌面</span>
        <button class="tsc-close" @click="close" aria-label="关闭">×</button>
      </div>
      <div class="tsc-body">
        <div ref="cardRef" class="ec-card" :class="cardCls">
          <ShareCardHeader variant="overlay" scene="塔罗 · TAROT" />
          <main class="ec-body">
            <div class="ec-u-intro">{{ nickname }} 抽到 <em>「{{ card?.name }}」</em>· {{ reversed ? '逆位' : '正位' }}</div>
            <div v-if="spreadName" class="tsc-spread">{{ (heroPosition && thumbs.length) ? heroPosition + ' · ' : '' }}{{ spreadName }}</div>

            <!-- ═ 正位: B1 悬浮牌面(真牌面居中聚光 + 红色正位章) ═ -->
            <div v-if="!reversed" class="t1-hero">
              <div class="t1-artwrap">
                <img class="t1-art" :src="image" :alt="card?.name || '塔罗牌'">
                <span class="t-bd t-bd-up">正位 UPRIGHT</span>
              </div>
              <div class="t1-nm">
                <span class="t1-cn ec-u-serif">{{ card?.name }}</span>
                <span v-if="enLine" class="t1-en">{{ enLine }}</span>
              </div>
              <div v-if="keywords.length" class="t1-kw">
                <template v-for="(k, i) in keywords" :key="i"><i v-if="i">·</i>{{ k }}</template>
              </div>
              <div v-if="metaLine" class="t1-meta">{{ metaLine }}</div>
            </div>

            <!-- ═ 逆位: B2 真实逆位(牌面 CSS rotate(180deg) + 黑色逆位章 + reversed 关键词语义) ═ -->
            <div v-else class="t2-hero">
              <div class="t2-artwrap">
                <img class="t2-art" :src="image" :alt="card?.name || '塔罗牌'">
                <span class="t-bd t-bd-rev">逆位 REVERSED</span>
              </div>
              <div class="t2-nm">
                <span class="t2-cn ec-u-serif">{{ card?.name }}</span>
                <span v-if="enLine" class="t2-en">{{ enLine }}</span>
              </div>
              <div v-if="keywords.length" class="t2-kw">
                <template v-for="(k, i) in keywords" :key="i"><i v-if="i">·</i>{{ k }}</template>
              </div>
              <div v-if="revNote" class="t2-note ec-u-kai">逆位提示 · <b>{{ revNote }}</b></div>
            </div>

            <!-- ═ 长牌义: B3 模式(2行clamp + 固定高度兜底) + 引导行 ═ -->
            <div v-if="card?.description" class="tsc-desc ec-u-kai">{{ card.description }}</div>
            <div class="tsc-hint">长按识别二维码 · 解读完整牌义</div>

            <!-- ═ 多牌牌阵: 其余牌缩略(不遮主牌, 全量展示, 逆位缩略同步 180°;
                 >4张(凯尔特十字9缩略)自动紧凑档: 缩小图与字号, flex-wrap 多行) ═ -->
            <div v-if="thumbs.length" class="tsc-thumbs" :class="{ compact: thumbs.length > 4 }">
              <div v-for="(t, i) in thumbs" :key="i" class="tsc-thumb">
                <img class="tsc-timg" :class="{ 'tsc-trev': t.reversed }" :src="t.image" :alt="t.name">
                <span v-if="t.position" class="tsc-tpos">{{ t.position }}</span>
                <span class="tsc-tname">{{ t.name }}{{ t.reversed ? '·逆' : '' }}</span>
              </div>
            </div>
          </main>
          <ShareInviteBand :visible="visible" :page="page" verb="邀你来抽一张牌" />
        </div>
      </div>
      <div class="tsc-foot">
        <button class="tsc-save" :disabled="saving" @click="save">{{ saving ? '生成中…' : '保存图片' }}</button>
        <button class="tsc-cancel" @click="close">关闭</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useUserStore } from '@/stores/user'
import ShareCardHeader from './ShareCardHeader.vue'
import ShareInviteBand from './ShareInviteBand.vue'
import { desensitizeNickname, tarotKeywords, tarotRevNote, tarotDesignation, tarotMetaLine, freezeCardLayout, waitQrLoaded } from './shareUtils'

const props = defineProps({
  visible: { type: Boolean, default: false },               // v-model:visible 弹窗开关
  card: { type: Object, default: null },                    // 完整牌义对象(majorArcana/minorArcanaComplete 条目): name/nameEn/element/keywords/upright/reversed(逆位关键词串)/astrology/description
  reversed: { type: Boolean, default: false },              // 正逆位。⚠️ 牌义对象的 reversed 字段是「逆位关键词串」, 方位只看本 prop
  image: { type: String, default: '' },                     // 主牌牌面图 URL
  extraCards: { type: Array, default: () => [] },           // 同阵其余牌缩略 [{ name, reversed, image, position }], 最多展示 4 张(四季牌阵 5 张全显)
  heroPosition: { type: String, default: '' },               // 主牌位次(现在/当前处境/核心指引…), 多牌阵展示
  spreadName: { type: String, default: '' },                // 牌阵名(单张牌阵/三张牌阵/凯尔特十字/四季牌阵)
  page: { type: String, default: 'tarot' },                 // QR 落地页
})
const emit = defineEmits(['update:visible'])

const userStore = useUserStore()
const cardRef = ref(null)
const saving = ref(false)

const nickname = computed(() => desensitizeNickname(userStore.user))
// 关键词: 正位取 keywords 前3(牌数据顺序); 逆位取 reversed 字段 split 前3 —— 两套内容语义
const keywords = computed(() => tarotKeywords(props.card, props.reversed))
// 逆位提示(B2 模式): reversed 前2词楷体行
const revNote = computed(() => (props.reversed ? tarotRevNote(props.card) : ''))
// 英文行(B1 'THE FOOL · 0' / B2 'DEATH · XIII' 模式): nameEn 大写 + 大阿卡纳罗马数字尾码
const enLine = computed(() => {
  const en = String(props.card?.nameEn || '').trim().toUpperCase()
  const desig = tarotDesignation(props.card)
  if (!en && !desig) return ''
  return [en, desig].filter(Boolean).join(' · ')
})
// 元素/星象小字行(B1 '风元素 · 天王星 · 大阿卡纳'; 小阿卡纳无 astrology → 显示牌组)
const metaLine = computed(() => tarotMetaLine(props.card))
const thumbs = computed(() => (props.extraCards || []).filter(t => t && t.image))
// 卡高: B1/B2 基础上叠加 牌阵行+2行牌义+引导行(+缩略图行); 375 宽等比, 实测算值勿动
const cardCls = computed(() => [props.reversed ? 'h-rev' : 'h-up', thumbs.value.length ? 'has-extras' : ''])

function close() { emit('update:visible', false) }

async function save() {
  if (!cardRef.value || saving.value) return
  saving.value = true
  await waitQrLoaded(cardRef.value)
  const unfreeze = freezeCardLayout(cardRef.value)
  try {
    const { default: html2canvas } = await import('html2canvas')
    const canvas = await html2canvas(cardRef.value, {
      scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false,
    })
    const link = document.createElement('a')
    link.download = '随心-塔罗占卜.png'
    link.href = canvas.toDataURL('image/png')
    link.click()
  } catch (e) {
    console.error('[TarotShareCard] 截图失败', e)
    alert('图片生成失败, 可截屏分享')
  } finally {
    unfreeze()
    saving.value = false
  }
}
</script>

<style scoped>
/* ——— 弹窗外壳(与 GuaShareCard 同构) ——— */
.tsc-mask { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.55); z-index: 2200; display: flex; align-items: center; justify-content: center; padding: 16px; }
.tsc-dialog { background: #fff; border-radius: 16px; width: min(92vw, 400px); max-height: 88vh; overflow: auto; box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25); }
.tsc-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px 10px; }
.tsc-title { font-size: 16px; font-weight: 700; }
.tsc-close { border: 0; background: none; font-size: 22px; color: #999; cursor: pointer; line-height: 1; padding: 0 4px; }
.tsc-body { padding: 0 16px; }
.tsc-foot { display: flex; gap: 10px; padding: 14px 16px 16px; }
.tsc-save { flex: 1; background: #e60023; color: #fff; border: 0; border-radius: 999px; padding: 11px 0; font-size: 15px; font-weight: 700; cursor: pointer; }
.tsc-save:disabled { opacity: 0.6; }
.tsc-cancel { background: none; border: 1.5px solid #ddd; border-radius: 999px; padding: 11px 18px; font-size: 15px; color: #888; cursor: pointer; }

/* ═══ 卡壳 + 设计令牌 + 共享原子 (gen.mjs SHELL_CSS 移植; 高度按 375 宽等比) ═══ */
.ec-card { --red: #e60023; --ink: #111; --paper: #fff; --paper-warm: #faf6f2;
  width: 100%; background: var(--paper); border: 2.5px solid var(--ink); border-radius: 14px;
  overflow: hidden; display: flex; flex-direction: column; position: relative; }
/* B1=575/B2=550 基础上 + 牌阵行(15)+2行牌义(52)+引导行(27)+缩略图行(~87)+邀请带(117) */
/* ⚠️ min-height 而非 aspect-ratio: aspect-ratio 算出的高度是定值, 内容超出不会撑高卡片
   ——invite band 曾溢出卡外被 overflow:hidden 裁掉(四季牌阵实测)。内容(牌图/描述/缩略)
   全为固定 px, 高度需求与卡宽无关, 用设计基准 px 做 min-height, 内容再多自然撑高 */
.ec-card.h-up { min-height: 676px; }
.ec-card.h-rev { min-height: 700px; }
.ec-card.has-extras.h-up { min-height: 760px; }
.ec-card.has-extras.h-rev { min-height: 786px; }
.ec-u-serif { font-family: 'Noto Serif CJK SC', 'AR PL UMing CN', serif; }
.ec-u-kai { font-family: 'AR PL UKai CN', '霞鹜文楷', 'LXGW WenKai', 'Noto Serif CJK SC', serif; }
.ec-u-intro { margin-top: 2px; font-size: 14.5px; font-weight: 700; color: #222; letter-spacing: 0.5px; }
.ec-u-intro em { font-style: normal; color: var(--red); }

/* Body 模块基座(预留 Header 覆盖层高度) — 自适应高度: 不锁 min-height/overflow,
   内容自然撑高卡片, hero 不被压缩(压缩是导出叠印的根源) */
.ec-body { flex: 1; padding: 54px 18px 12px; display: flex; flex-direction: column; position: relative; }

/* 牌阵名小字行(intro 下, 单牌阵也有) */
.tsc-spread { margin-top: 3px; font-size: 10.5px; color: #999; letter-spacing: 2px; }

/* ═══ 正逆位徽章 (gen.mjs TAROT_BADGE_CSS 逐字节移植) ═══ */
.t-bd { display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: 2px; border-radius: 999px; padding: 4px 12px; }
.t-bd-up { background: #e60023; color: #fff; }
.t-bd-rev { background: #111; color: #fff; }

/* ═══ B1 正位「悬浮牌面」 (gen.mjs t1-* CSS 逐字节移植) — 去掉 flex:1/min-height:0: 自适应高度下 hero 取自然高, 不参与压缩 */
.t1-hero { display: flex; flex-direction: column; align-items: center; justify-content: center; }
.t1-artwrap { position: relative; margin-top: 12px; }
.t1-art { height: 234px; width: auto; border-radius: 7px; box-shadow: 0 14px 30px rgba(0, 0, 0, 0.24); display: block; }
.t1-artwrap .t-bd { position: absolute; top: -11px; right: -15px; transform: rotate(3deg); }
.t1-nm { display: flex; align-items: baseline; gap: 9px; margin-top: 11px; max-width: 100%; }
.t1-cn { font-size: 29px; font-weight: 900; color: #111; letter-spacing: 2px; white-space: nowrap; flex-shrink: 0; }
.t1-en { font-size: 11px; color: #999; letter-spacing: 3px; font-weight: 600; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.t1-kw { margin-top: 9px; font-size: 14px; color: #222; letter-spacing: 1px; font-weight: 600; }
.t1-kw i { font-style: normal; color: #e60023; padding: 0 7px; }
.t1-meta { margin-top: 6px; font-size: 11px; color: #999; letter-spacing: 2px; }

/* ═══ B2 逆位「真实逆位」 (gen.mjs t2-* CSS 逐字节移植: 牌面物理 180° + 章在左侧 -3°) — 同 B1 去压缩 */
.t2-hero { display: flex; flex-direction: column; align-items: center; justify-content: center; }
.t2-artwrap { position: relative; margin-top: 28px; }  /* 章top:-11px探出, 需与上方intro拉开呼吸空间(窄卡实测曾压到标题行) */
.t2-art { height: 210px; width: auto; transform: rotate(180deg); border-radius: 7px; box-shadow: 0 14px 30px rgba(0, 0, 0, 0.24); display: block; }
.t2-artwrap .t-bd { position: absolute; top: -11px; left: -15px; transform: rotate(-3deg); }
.t2-nm { display: flex; align-items: baseline; gap: 9px; margin-top: 12px; max-width: 100%; flex-shrink: 0; }
.t2-cn { font-size: 29px; font-weight: 900; color: #111; letter-spacing: 2px; white-space: nowrap; flex-shrink: 0; }
.t2-en { font-size: 11px; color: #999; letter-spacing: 3px; font-weight: 600; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.t2-kw { margin-top: 8px; font-size: 14px; color: #222; letter-spacing: 1px; font-weight: 600; flex-shrink: 0; }
.t2-kw i { font-style: normal; color: #e60023; padding: 0 7px; }
.t2-note { margin-top: 7px; font-size: 12.5px; color: #666; letter-spacing: 1px; flex-shrink: 0; }
.t2-note b { color: #111; }

/* ═══ B3 长牌义 2行截断 + 引导行 (gen.mjs t3-desc/t3-hint 值移植) ═══
   ⚠️ html2canvas 对 -webkit-line-clamp 支持不稳 → clamp 之外加固定高度兜底(2行×12.5px×1.68=42px) */
.tsc-desc { margin-top: 10px; font-size: 12.5px; color: #555; line-height: 1.68; letter-spacing: 0.3px; text-align: justify;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; height: 42px; }
.tsc-hint { margin-top: 11px; text-align: center; font-size: 10.5px; color: #aaa; letter-spacing: 2px; }

/* ═══ 多牌牌阵缩略行(主牌下方, 不遮主体) — 全量展示: flex-wrap 换行, 卡高自适应;
   375视口卡内宽275px: 常规档4张/行(4×58+3×10=262 ✓), 紧凑档5张/行(5×42+4×8=242 ✓) */
.tsc-thumbs { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-top: 10px; flex-shrink: 0; row-gap: 12px; }
/* 紧凑档(凯尔特十字 9 缩略): 图 64→46px, 字号微缩, 一行可容 5 张, 共两行 */
.tsc-thumbs.compact { gap: 8px; row-gap: 10px; }
.tsc-thumbs.compact .tsc-timg { height: 46px; }
.tsc-thumbs.compact .tsc-tpos { font-size: 8px; letter-spacing: 0.5px; }
.tsc-thumbs.compact .tsc-tname { font-size: 8.5px; max-width: 56px; }
.tsc-thumb { display: flex; flex-direction: column; align-items: center; gap: 3px; min-width: 0; }
.tsc-timg { height: 64px; width: auto; border-radius: 4px; border: 1px solid rgba(17, 17, 17, 0.12); box-shadow: 0 4px 10px rgba(0, 0, 0, 0.16); display: block; }
.tsc-trev { transform: rotate(180deg); }
.tsc-tpos { font-size: 9px; color: #e60023; font-weight: 700; letter-spacing: 1px; white-space: nowrap; }
.tsc-tname { font-size: 9.5px; color: #999; letter-spacing: 1px; white-space: nowrap; max-width: 76px; overflow: hidden; text-overflow: ellipsis; }
</style>
